<template>
  <div class="layout-panel">
    <div class="panel-header">
      <div class="panel-title">
        <span class="title-icon">
          <i class="bi bi-grid-1x2" />
        </span>
        <p>Layout</p>
      </div>
      <button 
        class="dbh-btn dbh-btn--outline" 
        @click="addFixture"
      >
        <i class="bi bi-plus" /> Fixture
      </button>
    </div>
    <div class="panel-subtitle">
      Ordered left to right to match the canvas. Hover a fixture to reorder or remove.
    </div>
    <div class="fixtures-list">
      <transition-group 
        name="fixture" 
        tag="div" 
        style="display:contents"
      >
        <div
          v-for="(fixture, index) in fixtures"
          :key="fixture.id"
          class="fixture-card"
          :data-layout-fixture-key="fixture.key"
        >
          <div class="fixture-name-row">
            <!-- Collapse arrow -->
            <button
              v-b-toggle="`fixture-collapse-${fixture.id}`"
              class="fixture-collapse-toggle"
              type="button"
              title="Expand / Collapse"
            >
              <i class="bi bi-chevron-right" />
            </button>

            <input
              v-model="fixture.name"
              class="fixture-name-input"
              :placeholder="'Fixture ' + (index + 1)"
            >

            <button
              class="btn-move-fixture"
              title="Move Left"
              :disabled="index === 0"
              @click="moveLeft(index)"
            >
              <i class="bi bi-chevron-up" />
            </button>

            <button
              class="btn-move-fixture"
              title="Move Right"
              :disabled="index === fixtures.length - 1"
              @click="moveRight(index)"
            >
              <i class="bi bi-chevron-down" />
            </button>

            <button
              class="btn-remove-fixture"
              title="Remove"
              @click="removeFixture(index)"
            >
              <i class="bi bi-trash" />
            </button>
          </div>

          <!-- Only this part collapses -->
          <b-collapse
            :id="`fixture-collapse-${fixture.id}`"
            visible
          >
            <div class="fixture-fields">
              <div class="field-item">
                <span class="field-label">Left %</span>

                <input
                  v-model.number="fixture.left"
                  class="field-input"
                  type="number"
                  step="0.1"
                >
              </div>

              <div class="field-item">
                <span class="field-label">Width %</span>

                <input
                  v-model.number="fixture.width"
                  class="field-input"
                  type="number"
                  step="0.1"
                >
              </div>

              <div class="field-item">
                <span class="field-label">Top %</span>

                <input
                  v-model.number="fixture.top"
                  class="field-input"
                  type="number"
                  step="0.1"
                >
              </div>

              <div class="field-item">
                <span class="field-label">Bottom %</span>

                <input
                  v-model.number="fixture.bottom"
                  class="field-input"
                  type="number"
                  step="0.1"
                >
              </div>
            </div>

            <div class="shelves-row">
              <div class="shelves-control">
                <span class="shelves-label">Shelves</span>

                <button
                  class="shelf-btn"
                  @click="decrementShelves(fixture)"
                >
                  −
                </button>

                <span class="shelf-count">
                  {{ fixture.shelves }}
                </span>

                <button
                  class="shelf-btn"
                  @click="incrementShelves(fixture)"
                >
                  +
                </button>
              </div>

              <button
                class="btn-reset"
                @click="resetFixture(fixture, index)"
              >
                Reset
              </button>
            </div>

            <div class="perspective-control">
              <div class="perspective-control__label">
                <span>Angle</span>
                <span>{{ formatDegrees(fixture.angle) }}</span>
              </div>
              <div class="range-row">
                <input
                  v-model.number="fixture.angle"
                  class="fine-tune-range"
                  type="range"
                  :min="MIN_FIXTURE_ANGLE"
                  :max="MAX_FIXTURE_ANGLE"
                  step="0.5"
                  @input="setFixtureAngle(fixture)"
                >
                <span class="range-end-label">Flat</span>
              </div>
              <div class="corners-row">
                <span>Corners</span>
                <span class="corners-status">
                  {{ fixture.isCustomPerspective ? 'Custom perspective' : 'Angle perspective' }}
                </span>
                <button
                  class="inline-reset"
                  type="button"
                  @click="resetFixtureCorners(fixture)"
                >
                  Reset
                </button>
              </div>
            </div>

            <div
              v-if="fixture.shelfFineTuning.length"
              class="shelf-fine-tuning"
            >
              <div class="shelf-fine-tuning__title">
                <span
                  v-b-toggle="`shelf-tuning-${fixture.id}`"
                  class="shelf-fine-tuning__title-text"
                >
                  Shelf fine-tuning
                </span>

                <button
                  v-b-toggle="`shelf-tuning-${fixture.id}`"
                  class="fixture-collapse-toggle"
                  type="button"
                  title="Expand / Collapse"
                >
                  <i class="bi bi-chevron-right" />
                </button>
              </div>
              <b-collapse
                :id="`shelf-tuning-${fixture.id}`"
                visible
              >
                <div
                  v-for="(shelf, shelfIndex) in fixture.shelfFineTuning"
                  :key="shelf.id || shelfIndex"
                  class="shelf-tuning-row"
                  :data-layout-shelf-id="shelf.id"
                >
                  <div class="d-flex align-items-center">
                    <strong class="mr-auto">Shelf {{ shelfIndex + 1 }}</strong>
                    <button
                      type="button"
                      class="btn-move-fixture"
                      title="Move shelf up"
                      aria-label="Move shelf up"
                      :disabled="shelfIndex === 0"
                      @click="$emit('move-shelf', { fixtureKey: fixture.key, shelfId: shelf.id, direction: -1 })"
                    >
                      <i class="bi bi-chevron-up" />
                    </button>
                    <button
                      type="button"
                      class="btn-move-fixture"
                      title="Move shelf down"
                      aria-label="Move shelf down"
                      :disabled="shelfIndex === fixture.shelfFineTuning.length - 1"
                      @click="$emit('move-shelf', { fixtureKey: fixture.key, shelfId: shelf.id, direction: 1 })"
                    >
                      <i class="bi bi-chevron-down" />
                    </button>
                  </div>
                  <div class="shelf-tuning-control">
                    <span>Y pos</span>
                    <input
                      v-model.number="shelf.yOffset"
                      class="fine-tune-range"
                      type="range"
                      :min="MIN_SHELF_Y_OFFSET"
                      :max="MAX_SHELF_Y_OFFSET"
                      step="0.5"
                    >
                    <span class="fine-tune-value">{{ formatNumber(shelf.yOffset) }}</span>
                    <button
                      class="inline-reset"
                      type="button"
                      @click="shelf.yOffset = 0"
                    >
                      Reset
                    </button>
                  </div>
                  <div class="shelf-tuning-control">
                    <span>Height</span>
                    <input
                      v-model.number="shelf.heightPercent"
                      class="fine-tune-range"
                      type="range"
                      :min="MIN_SHELF_AREA_HEIGHT_PERCENT"
                      :max="getShelfHeightMaxPercent(shelf)"
                      step="0.5"
                    >
                    <span class="fine-tune-value">{{ formatPercent(shelf.heightPercent) }}</span>
                    <button
                      class="inline-reset"
                      type="button"
                      @click="resetShelfHeight(fixture, shelf)"
                    >
                      Reset
                    </button>
                  </div>
                  <div class="shelf-tuning-control">
                    <span>Skew</span>
                    <input
                      :value="getShelfEffectiveAngle(fixture, shelf)"
                      class="fine-tune-range"
                      type="range"
                      :min="MIN_SHELF_SKEW"
                      :max="MAX_SHELF_SKEW"
                      step="0.5"
                      @input="setShelfEffectiveAngle(fixture, shelf, $event.target.value)"
                    >
                    <span class="fine-tune-value">{{ formatDegrees(getShelfEffectiveAngle(fixture, shelf)) }}</span>
                    <button
                      class="inline-reset"
                      type="button"
                      @click="shelf.skew = 0"
                    >
                      Reset
                    </button>
                  </div>
                </div>
              </b-collapse>
            </div>
          </b-collapse>
        </div>
        <div 
          v-if="fixtures.length === 0" 
          key="empty"
          class="empty-state" 
        >
          <i class="bi bi-layout-three-columns" />
          No fixtures yet. Click <strong>+ Fixture</strong> to add one.
        </div>
      </transition-group>
    </div>
    <div class="panel-footer">
      <button 
        class="btn-restore"
        @click="restoreDefaults"
      >
        Restore defaults
      </button>
    </div>
  </div>
</template>

<script>
import {
  DEFAULT_LAYOUT_FIXTURES,
  DEFAULT_SHELF_COUNT,
  MAX_FIXTURE_ANGLE,
  MAX_SHELF_SKEW,
  MAX_SHELF_Y_OFFSET,
  MIN_FIXTURE_ANGLE,
  MIN_SHELF_SKEW,
  MIN_SHELF_Y_OFFSET,
  cloneDisplayBuilderValue,
  defaultFixtureState,
  fixtureCornersFromAngle,
  getFixtureAngle,
  getFixtureOrder,
  getShelvesInVerticalOrder,
  getShelfPerspectiveGeometry,
  normalizeFixtureCorners,
  normalizeDisplayBuilderState,
  percentFromRect,
  rectFromPercent,
  slugifyFixtureKey,
} from '@frontend/components/display-builder/layout-defaults';

let nextId = 10;
const MIN_SHELF_AREA_HEIGHT_PERCENT = 2;
const MAX_SHELF_AREA_HEIGHT_PERCENT = 100;

export default {
  name: 'EditLayout',
  props: {
    layoutState: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      MAX_FIXTURE_ANGLE,
      MAX_SHELF_AREA_HEIGHT_PERCENT,
      MAX_SHELF_SKEW,
      MAX_SHELF_Y_OFFSET,
      MIN_FIXTURE_ANGLE,
      MIN_SHELF_AREA_HEIGHT_PERCENT,
      MIN_SHELF_SKEW,
      MIN_SHELF_Y_OFFSET,
      isSyncingFromState: false,
      fixtures: this.createFixturesFromState(this.layoutState),
    };
  },
  watch: {
    layoutState: {
      deep: true,
      handler(value) {
        this.isSyncingFromState = true;
        this.fixtures = this.createFixturesFromState(value);
        this.$nextTick(() => {
          this.isSyncingFromState = false;
        });
      },
    },
    fixtures: {
      deep: true,
      handler() {
        if (this.isSyncingFromState) {
          return;
        }

        this.emitChange();
      },
    },
  },
  mounted() {
    window.addEventListener('keydown', this.onDeleteKeydown, true);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onDeleteKeydown, true);
  },
  methods: {
    onDeleteKeydown(event) {
      if (
        event.key !== 'Delete'
        || event.defaultPrevented
        || event.repeat
        || !window.displayBuilderIsLayoutEditing
      ) {
        return;
      }

      const target = event.target;
      const isEditableTarget = target instanceof Element && (
        target.matches('input, textarea, select')
        || target.isContentEditable
        || !!target.closest('[contenteditable="true"]')
      );

      if (isEditableTarget) {
        return;
      }

      const fixtureKey = window.displayBuilderHelper?.activeFixture;

      if (!fixtureKey || !this.removeFixtureByKey(fixtureKey)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
    },
    findFixtureCard(fixtureKey) {
      return Array.from(this.$el?.querySelectorAll?.('[data-layout-fixture-key]') || [])
        .find((element) => String(element.dataset.layoutFixtureKey) === String(fixtureKey)) || null;
    },
    revealCollapse(collapseId) {
      const collapse = Array.from(this.$el?.querySelectorAll?.('.collapse') || [])
        .find((element) => element.id === collapseId);

      if (collapse && !collapse.classList.contains('show')) {
        this.$root.$emit('bv::toggle::collapse', collapseId);
      }
    },
    scrollLayoutElementIntoView(element) {
      if (!element) {
        return;
      }

      element.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    },
    scrollFixtureIntoView(fixtureKey) {
      const fixture = this.fixtures.find((item) => String(item.key) === String(fixtureKey));
      const card = this.findFixtureCard(fixtureKey);

      if (!fixture || !card) {
        return;
      }

      this.revealCollapse(`fixture-collapse-${fixture.id}`);
      this.$nextTick(() => this.scrollLayoutElementIntoView(card));
    },
    scrollShelfIntoView(fixtureKey, shelfId) {
      const fixture = this.fixtures.find((item) => String(item.key) === String(fixtureKey));
      const card = this.findFixtureCard(fixtureKey);

      if (!fixture || !card) {
        return;
      }

      this.revealCollapse(`fixture-collapse-${fixture.id}`);
      this.revealCollapse(`shelf-tuning-${fixture.id}`);

      this.$nextTick(() => {
        const shelfRow = Array.from(card.querySelectorAll('[data-layout-shelf-id]'))
          .find((element) => String(element.dataset.layoutShelfId) === String(shelfId));

        this.scrollLayoutElementIntoView(shelfRow || card);
      });
    },
    createFixturesFromState(state) {
      const normalized = normalizeDisplayBuilderState(state || defaultFixtureState());
      const order = getFixtureOrder(normalized);

      return order.map((key, index) => {
        const fixture = normalized.fixtures[key] || {};
        const defaults = DEFAULT_LAYOUT_FIXTURES[index] || DEFAULT_LAYOUT_FIXTURES[0];
        const rect = fixture.rect || rectFromPercent(defaults);
        const percent = percentFromRect(rect);
        const corners = normalizeFixtureCorners(fixture.corners, rect);
        const angle = getFixtureAngle(corners, rect);
        const angleCorners = fixtureCornersFromAngle(rect, angle);
        const shelfCount = Number(
          fixture.shelfCount || fixture.shelves?.length || defaults.shelfCount || DEFAULT_SHELF_COUNT
        );

        return {
          id: fixture.id || key,
          key,
          name: fixture.label || defaults.label || `Fixture ${index + 1}`,
          left: percent.left,
          width: percent.width,
          top: percent.top,
          bottom: percent.bottom,
          shelves: shelfCount,
          angle,
          corners,
          sourceRect: rect,
          isCustomPerspective: this.areCornersDifferent(corners, angleCorners),
          shelfFineTuning: getShelvesInVerticalOrder(fixture.shelves || [], rect).map((shelf, shelfIndex) => ({
            id: shelf.id || `shelf-${shelfIndex + 1}`,
            heightPercent: this.getShelfHeightPercent(rect, shelf, shelfCount),
            yOffset: Number(shelf.yOffset || 0),
            skew: Number(shelf.skew || 0),
            sourceLayout: {
              x: shelf.x,
              y: shelf.y,
              width: shelf.width,
              height: shelf.height,
            },
          })),
        };
      });
    },
    areCornersDifferent(first, second, tolerance = 0.5) {
      return ['tl', 'tr', 'br', 'bl'].some((key) => {
        return Math.abs(Number(first?.[key]?.x || 0) - Number(second?.[key]?.x || 0)) > tolerance
          || Math.abs(Number(first?.[key]?.y || 0) - Number(second?.[key]?.y || 0)) > tolerance;
      });
    },
    transformCornersToRect(corners, sourceRect, nextRect) {
      const normalized = normalizeFixtureCorners(corners, sourceRect);
      const scaleX = Number(nextRect.width || 1) / Math.max(1, Number(sourceRect.width || 1));
      const scaleY = Number(nextRect.height || 1) / Math.max(1, Number(sourceRect.height || 1));

      return Object.fromEntries(Object.entries(normalized).map(([key, point]) => [
        key,
        {
          x: nextRect.left + (point.x - sourceRect.left) * scaleX,
          y: nextRect.top + (point.y - sourceRect.top) * scaleY,
        },
      ]));
    },
    getShelfRenderGeometry(fixture, shelf) {
      const rect = rectFromPercent(fixture);
      const corners = fixture.isCustomPerspective
        ? this.transformCornersToRect(fixture.corners, fixture.sourceRect || rect, rect)
        : fixtureCornersFromAngle(rect, fixture.angle);

      return getShelfPerspectiveGeometry({ rect, corners }, {
        ...(shelf.sourceLayout || {}),
        height: this.getShelfHeight(fixture, shelf),
        yOffset: Number(shelf.yOffset || 0),
        skew: Number(shelf.skew || 0),
      });
    },
    getShelfHeightPercent(rect, shelf, shelfCount = DEFAULT_SHELF_COUNT) {
      const fixtureHeight = Math.max(1, Number(rect?.height || 1));
      const defaultHeight = fixtureHeight / Math.max(1, Number(shelfCount || 1));
      const shelfHeight = Math.max(1, Number(shelf?.height || defaultHeight));

      return Math.max(MIN_SHELF_AREA_HEIGHT_PERCENT, shelfHeight / fixtureHeight * 100);
    },
    getShelfHeightMaxPercent(shelf) {
      return Math.max(
        MAX_SHELF_AREA_HEIGHT_PERCENT,
        Math.ceil(Number(shelf.heightPercent || 0))
      );
    },
    getShelfHeight(fixture, shelf) {
      const rect = rectFromPercent(fixture);
      const heightPercent = Math.max(
        MIN_SHELF_AREA_HEIGHT_PERCENT,
        Number(shelf.heightPercent || MIN_SHELF_AREA_HEIGHT_PERCENT)
      );

      return Math.max(1, rect.height * heightPercent / 100);
    },
    resetShelfHeight(fixture, shelf) {
      shelf.heightPercent = Math.min(
        MAX_SHELF_AREA_HEIGHT_PERCENT,
        Math.max(
          MIN_SHELF_AREA_HEIGHT_PERCENT,
          100 / Math.max(1, Number(fixture.shelves || 1))
        )
      );
    },
    getShelfAutomaticAngle(fixture, shelf) {
      return this.getShelfRenderGeometry(fixture, shelf).perspectiveAngle;
    },
    getShelfEffectiveAngle(fixture, shelf) {
      return this.getShelfRenderGeometry(fixture, shelf).angle;
    },
    setShelfEffectiveAngle(fixture, shelf, value) {
      const desiredAngle = Number(value);

      if (!Number.isFinite(desiredAngle)) {
        return;
      }

      const baseline = this.getShelfRenderGeometry(fixture, {
        ...shelf,
        skew: 0,
      });
      const points = baseline.points;
      const horizontalRun = baseline.rightCenter.x - baseline.leftCenter.x;
      const horizontalRise = baseline.rightCenter.y - baseline.leftCenter.y;
      const averageWidth = (
        Math.hypot(points.tr.x - points.tl.x, points.tr.y - points.tl.y)
        + Math.hypot(points.br.x - points.bl.x, points.br.y - points.bl.y)
      ) / 2;
      const desiredRise = Math.tan(desiredAngle * Math.PI / 180) * horizontalRun;
      const adjustment = Math.atan2(desiredRise - horizontalRise, Math.max(1, averageWidth))
        * 180 / Math.PI;

      shelf.skew = Math.min(MAX_SHELF_SKEW, Math.max(MIN_SHELF_SKEW, adjustment));
    },
    emitChange() {
      const usedKeys = new Set();
      const fixtures = this.fixtures.map((fixture, index) => {
        const fallbackKey = `fixture_${index + 1}`;
        let key = fixture.key || slugifyFixtureKey(fixture.name, fallbackKey);

        while (usedKeys.has(key)) {
          key = `${key}_${index + 1}`;
        }

        usedKeys.add(key);

        const rect = rectFromPercent(fixture);
        const corners = fixture.isCustomPerspective
          ? this.transformCornersToRect(fixture.corners, fixture.sourceRect || rect, rect)
          : fixtureCornersFromAngle(rect, fixture.angle);

        return {
          ...fixture,
          key,
          label: fixture.name || `Fixture ${index + 1}`,
          rect,
          corners,
          angle: getFixtureAngle(corners, rect),
          shelfCount: Number(fixture.shelves || 1),
          shelves: fixture.shelfFineTuning.map((shelf) => ({
            id: shelf.id,
            height: this.getShelfHeight(fixture, shelf),
            yOffset: Number(shelf.yOffset || 0),
            skew: Number(shelf.skew || 0),
          })),
        };
      });

      this.$emit('change', fixtures);
      this.$emit('input', fixtures);
    },
    addFixture() {
      const index = this.fixtures.length + 1;
      this.fixtures.push({
        id: `fixture-${nextId++}`,
        key: `fixture_${Date.now()}_${index}`,
        name: `Fixture ${index}`,
        left: 0,
        width: 20,
        top: 10,
        bottom: 90,
        shelves: DEFAULT_SHELF_COUNT,
        angle: 0,
        corners: fixtureCornersFromAngle(rectFromPercent({ left: 0, width: 20, top: 10, bottom: 90 }), 0),
        sourceRect: rectFromPercent({ left: 0, width: 20, top: 10, bottom: 90 }),
        isCustomPerspective: false,
        shelfFineTuning: [],
      });
    },
    removeFixture(index) {
      this.fixtures.splice(index, 1);
    },
    removeFixtureByKey(fixtureKey) {
      const index = this.fixtures.findIndex((fixture) => {
        return String(fixture.key) === String(fixtureKey);
      });

      if (index === -1) {
        return false;
      }

      this.removeFixture(index);
      return true;
    },
    moveLeft(index) {
      if (index <= 0) {
        return;
      }

      this.moveFixture(index, index - 1);
    },
    moveRight(index) {
      if (index >= this.fixtures.length - 1) {
        return;
      }

      this.moveFixture(index, index + 1);
    },
    moveFixture(fromIndex, toIndex) {
      const slots = this.fixtures.map((fixture) => ({
        left: fixture.left,
      }));
      const nextFixtures = this.fixtures.map((fixture) => ({ ...fixture }));
      const [fixture] = nextFixtures.splice(fromIndex, 1);

      nextFixtures.splice(toIndex, 0, fixture);

      this.fixtures = nextFixtures.map((nextFixture, index) => {
        return {
          ...nextFixture,
          ...slots[index],
        };
      });
    },
    incrementShelves(fixture) {
      fixture.shelves = Math.min(fixture.shelves + 1, 20);
    },
    decrementShelves(fixture) {
      fixture.shelves = Math.max(fixture.shelves - 1, 1);
    },
    setFixtureAngle(fixture) {
      const rect = rectFromPercent(fixture);

      fixture.isCustomPerspective = false;
      fixture.sourceRect = rect;
      fixture.corners = fixtureCornersFromAngle(rect, fixture.angle);
    },
    resetFixtureCorners(fixture) {
      fixture.angle = 0;
      this.setFixtureAngle(fixture);
    },
    formatDegrees(value) {
      return `${this.formatNumber(value)}°`;
    },
    formatPercent(value) {
      return `${this.formatNumber(value)}%`;
    },
    formatNumber(value) {
      const number = Number(value || 0);

      return Number.isInteger(number) ? number.toFixed(1) : number.toFixed(1);
    },
    resetFixture(fixture, index) {
      const def = DEFAULT_LAYOUT_FIXTURES[index];
      if (def) {
        const rect = rectFromPercent(def);

        Object.assign(fixture, {
          ...cloneDisplayBuilderValue(def),
          id: fixture.id,
          key: def.key,
          name: def.label,
          shelves: def.shelfCount,
          angle: 0,
          corners: fixtureCornersFromAngle(rect, 0),
          sourceRect: rect,
          isCustomPerspective: false,
          shelfFineTuning: fixture.shelfFineTuning.map((shelf) => ({
            ...shelf,
            heightPercent: 100 / Math.max(1, Number(def.shelfCount || 1)),
            yOffset: 0,
            skew: 0,
          })),
        });
      } else {
        Object.assign(fixture, {
          left: 0,
          width: 20,
          top: 10,
          bottom: 90,
          shelves: DEFAULT_SHELF_COUNT,
          angle: 0,
          isCustomPerspective: false,
          shelfFineTuning: fixture.shelfFineTuning.map((shelf) => ({
            ...shelf,
            heightPercent: 100 / DEFAULT_SHELF_COUNT,
            yOffset: 0,
            skew: 0,
          })),
        });
        this.setFixtureAngle(fixture);
      }
    },
    restoreDefaults() {
      this.fixtures = this.createFixturesFromState(defaultFixtureState());
    },
  },
};
</script>
<style scoped>
  .layout-panel {
    --panel-bg: #1abc9c05;
    --panel-border: #e2e8f0;
    --panel-shadow: 0 2px 12px rgba(0,0,0,0.07), 0 1px 3px rgba(0,0,0,0.05);
    --fixture-bg: #f8fafc;
    --fixture-border: #dde3ed;
    --fixture-hover: #eef2f7;
    --input-bg: #ffffff;
    --input-border: #d1d9e6;
    --input-focus: #5a8dee;
    --label-color: #64748b;
    --title-color: #1e293b;
    --accent: #5a8dee;
    --accent-light: #eff4ff;
    --danger: #ef4444;
    --text-muted: #94a3b8;
    --btn-hover: #f1f5f9;
    --shelf-btn-bg: #eef2f7;
    --shelf-count-color: #1e293b;
    --reset-color: #94a3b8;
    --reset-hover: #ef4444;
    --scrollbar-thumb: #cbd5e1;
    --add-btn-bg: #1e293b;
    --add-btn-color: #ffffff;
    --add-btn-hover: #334155;
    --restore-color: #5a8dee;
    --teal:       #2eb89a;
  }

  .layout-panel {
    background-color: #1abc9c05;
    border: 1px solid #1abc9c66;
    border-radius: 8px;
    overflow: hidden;
    margin-bottom: 10px;
  }

  .panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px 14px;
    background-color: #1abc9c05;
  }

  .panel-title {
    display: flex;
    align-items: center;
    gap: 9px;
    font-size: 15px;
    font-weight: 600;
    color: var(--title-color);
    letter-spacing: -0.2px;
  }

  .panel-title p {
    margin: 0;
    font-size: 0.75rem;
  }

  .panel-title .title-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 7px;
    background: var(--accent-light);
  }

  .panel-title .title-icon i {
    font-size: 14px;
    color: var(--teal);
  }

  .btn-add-fixture {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 7px 14px;
    font-size: 13px;
    font-weight: 500;
    font-family: 'DM Sans', sans-serif;
    background: var(--add-btn-bg);
    color: var(--add-btn-color);
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.18s, transform 0.12s;
    letter-spacing: -0.1px;
  }
  .btn-add-fixture:hover {
    background: var(--add-btn-hover);
    transform: translateY(-1px);
  }
  .btn-add-fixture:active { transform: translateY(0); }

  .panel-subtitle {
    padding: 0px 20px;
    font-size: 12px;
    color: var(--label-color);
    background: #fcfcfd;
    letter-spacing: 0.1px;
    background-color: #1abc9c05;
  }

  .fixtures-list {
    max-height: 520px;
    overflow-y: auto;
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    background-color: #1abc9c05;
  }

  .fixtures-list::-webkit-scrollbar { width: 6px; }
  .fixtures-list::-webkit-scrollbar-track { background: transparent; }
  .fixtures-list::-webkit-scrollbar-thumb {
    background: var(--scrollbar-thumb);
    border-radius: 99px;
  }

  .fixture-card {
    background: #1abc9c05;
    border: 1.5px solid var(--fixture-border);
    border-radius: 10px;
    overflow: hidden;
    transition: border-color 0.2s, box-shadow 0.2s;
    flex-shrink: 0;
  }
  .fixture-card:hover {
    border-color: #c5d0e6;
    box-shadow: 0 2px 8px rgba(90,141,238,0.07);
  }

  .fixture-name-row {
    display: flex;
    align-items: center;
    padding: 9px 12px 9px 12px;
    gap: 8px;
  }

  .drag-handle {
    color: var(--text-muted);
    cursor: grab;
    font-size: 13px;
    opacity: 0.5;
    transition: opacity 0.15s;
    flex-shrink: 0;
  }
  .fixture-card:hover .drag-handle { opacity: 0.9; }

  .fixture-name-input {
    flex: 1;
    border: 1px solid var(--input-border);
    font-size: 13.5px;
    color: var(--title-color);
    outline: none;
    padding: 0;
    min-width: 0;
    padding: 5px 8px;
    border-radius: 6px;
  }
  .fixture-name-input::placeholder { color: var(--text-muted); }

  .btn-remove-fixture, .btn-move-fixture {
    background: none;
    border: none;
    padding: 2px 4px;
    cursor: pointer;
    color: var(--text-muted);
    font-size: 13px;
    border-radius: 5px;
    transition: color 0.15s, background 0.15s;
    display: flex;
    align-items: center;
    opacity: 0;
    transition: opacity 0.15s, color 0.15s, background 0.15s;
  }

  .fixture-card:hover .btn-remove-fixture,
  .fixture-card:hover .btn-move-fixture:not(:disabled) {
    opacity: 1;
  }
  .btn-remove-fixture:hover {
    color: var(--danger);
    background: #fee2e2;
  }

  .fixture-card:hover .btn-move-fixture:hover {
    color: #3c4b64;
  }

  .fixture-card:hover .btn-move-fixture:disabled {
    opacity: 0.25;
    cursor: default;
    pointer-events: none;
  }

  .fixture-card:hover .btn-move-fixture:disabled:hover {
    color: var(--text-muted);
    background: none;
  }

  .fixture-fields {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 0;
    padding: 10px 12px 4px;
  }

  .field-item {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 5px 6px 5px 0;
    min-width: 0;
  }
  .field-item:nth-child(odd) { padding-right: 10px; }

  .field-label {
    font-size: 11.5px;
    font-weight: 500;
    color: var(--label-color);
    white-space: nowrap;
    min-width: 54px;
    letter-spacing: 0.1px;
  }

  .field-input {
    flex: 1;
    border: 1px solid var(--input-border);
    border-radius: 6px;
    padding: 5px 8px;
    font-family: 'DM Mono', monospace;
    font-size: 12.5px;
    color: var(--title-color);
    background: var(--input-bg);
    outline: none;
    transition: border-color 0.18s, box-shadow 0.18s;
    min-width: 0;
    width: 0;
  }
  .field-input:focus {
    border-color: var(--input-focus);
    box-shadow: 0 0 0 3px rgba(90,141,238,0.13);
  }

  /* ── Shelves row ── */
  .shelves-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px 10px;
  }

  .shelves-control {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .shelves-label {
    font-size: 11.5px;
    font-weight: 500;
    color: var(--label-color);
    letter-spacing: 0.1px;
    margin-right: 2px;
  }

  .shelf-btn {
    width: 26px;
    height: 26px;
    border-radius: 6px;
    border: 1.5px solid var(--fixture-border);
    background: #fff;
    color: var(--title-color);
    font-size: 15px;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s, transform 0.1s;
    padding: 0;
  }
  .shelf-btn:hover {
    background: var(--accent-light);
    border-color: var(--accent);
    color: var(--accent);
  }
  .shelf-btn:active { transform: scale(0.92); }

  .shelf-count {
    font-family: 'DM Mono', monospace;
    font-size: 13px;
    font-weight: 500;
    color: var(--shelf-count-color);
    min-width: 18px;
    text-align: center;
  }

  .btn-reset {
    background: none;
    border: none;
    padding: 0;
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    color: var(--reset-color);
    cursor: pointer;
    letter-spacing: 0.1px;
    transition: color 0.15s;
  }
  .btn-reset:hover { color: var(--reset-hover); }

  /* ── Panel footer ── */
  .panel-footer {
    padding: 12px 20px;
    border-top: 1.5px solid var(--panel-border);
    background-color: #1abc9c05;
  }

  .btn-restore {
    background: none;
    border: none;
    padding: 0;
    font-size: 13px;
    color: var(--panel-color);
    cursor: pointer;
    font-weight: 500;
    letter-spacing: -0.1px;
    transition: color 0.15s, text-decoration 0.15s;
  }
  .btn-restore:hover {
    color: #3b6fd4;
    text-decoration: underline;
  }

  /* ── Empty state ── */
  .empty-state {
    text-align: center;
    padding: 28px 20px;
    color: var(--text-muted);
    font-size: 13px;
  }
  .empty-state i { font-size: 28px; margin-bottom: 8px; display: block; opacity: 0.4; }

  /* ── Transitions ── */
  .fixture-enter-active, .fixture-leave-active {
    transition: opacity 0.2s, transform 0.2s;
  }
  .fixture-enter { opacity: 0; transform: translateY(-8px); }
  .fixture-leave-to { opacity: 0; transform: translateY(-4px); }

  .dbh-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: var(--font);
    font-size: 13px;
    font-weight: 500;
    border-radius: var(--radius-sm);
    border: 1px solid transparent;
    padding: 0.5rem 1rem;
    cursor: pointer;
    white-space: nowrap;
    transition: background .14s, border-color .14s, color .14s, box-shadow .14s;
    line-height: 1;
}
.dbh-btn--outline {
    background: var(--bg-white);
    border-color: var(--border);
    color: var(--text-base);
}
.fixture-collapse-toggle {
  width: 18px;
  height: 26px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;
}

.fixture-collapse-toggle i {
  font-size: 10px;
  transition: transform 0.2s ease;
}

.fixture-collapse-toggle.not-collapsed i {
  transform: rotate(90deg);
}

.fixture-collapse-toggle:hover {
  color: var(--title-color);
}

.perspective-control,
.shelf-fine-tuning {
  padding: 10px 12px;
  border-top: 1px solid var(--panel-border);
}

.perspective-control__label,
.corners-row,
.shelf-tuning-control {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--label-color);
  font-size: 11px;
}

.perspective-control__label {
  justify-content: space-between;
  margin-bottom: 4px;
}

.range-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.fine-tune-range {
  min-width: 0;
  flex: 1;
  height: 4px;
  accent-color: var(--teal);
}

.range-end-label,
.fine-tune-value {
  min-width: 34px;
  color: var(--label-color);
  font-size: 10px;
  text-align: right;
}

.corners-row {
  margin-top: 8px;
}

.corners-status {
  flex: 1;
  color: var(--text-muted);
}

.inline-reset {
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  font-size: 10px;
}

.inline-reset:hover {
  color: var(--teal);
}

.shelf-fine-tuning__title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--label-color);
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.shelf-tuning-row {
  padding: 8px 0;
  border-top: 1px solid rgba(226, 232, 240, 0.75);
  color: var(--title-color);
  font-size: 11px;
}

.shelf-tuning-row:first-of-type {
  border-top: 0;
}

.shelf-tuning-control {
  margin-top: 6px;
}

.shelf-tuning-control > span:first-child {
  width: 48px;
  flex: 0 0 48px;
}
.shelf-fine-tuning__title-text {
  flex: 1;
  cursor: pointer;
}
</style>
